import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark, Share2, Timer } from "lucide-react";
import { toast } from "react-toastify";
import star from "../../assets/img/star.svg";
import { surveyPhoto, handlePhotoError } from "../../utils/profilePhoto";
import photolg from "../../assets/img/photo-1.jpg";
import { getSurveyForMe, saveSurvey } from "../../service/surveyForMe";

// Helper function to format the created_on date to a user-friendly relative or local date
const formatPostedDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;

  if (isNaN(diffMs)) return ""; // Invalid date handling

  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) {
    return `${Math.max(1, diffMins)} Min Ago`;
  } else if (diffHrs < 24) {
    return `${diffHrs} ${diffHrs === 1 ? "Hr" : "Hrs"} Ago`;
  } else if (diffDays < 7) {
    return `${diffDays} ${diffDays === 1 ? "Day" : "Days"} Ago`;
  } else {
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }
};


const Dashboard = () => {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());

  useEffect(() => {
    let isMounted = true;

    const fetchSurveys = async () => {
      try {
        setLoading(true);
        const response = await getSurveyForMe();
        if (!isMounted) return;

        if (response && response.data && response.data.success) {
          setSurveys(response.data.data || []);
        } else {
          setSurveys([]);
        }
      } catch (err) {
        console.error("Error fetching surveys in feed dashboard:", err);
        if (isMounted) setSurveys([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchSurveys();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleStart = (item) => {
    // Map data matching expectations in TakeSurvey
    const mappedMeta = {
      id: item.id,
      author: {
        name: "Product Researcher",
        avatar: surveyPhoto(item),
      },
      postedAgo: formatPostedDate(item.created_on),
      coverImage: item.survey_image && item.survey_image !== "string" ? item.survey_image : photolg,
      title: item.survey_title || "Untitled Survey",
      description: item.survey_description || "No description provided.",
      rewardCoins: parseFloat(item.reward_per_participant) || 0,
      tags: [item.sector, item.industry, item.country].filter(Boolean),
    };
    navigate(`/take-survey/${item.id}`, { state: { surveyMeta: mappedMeta } });
  };

  const handleSave = async (surveyId) => {
    if (savingId) return;
    try {
      setSavingId(surveyId);
      const response = await saveSurvey({ survey_id: surveyId });
      if (response && response.data && response.data.success) {
        setSavedIds((prev) => {
          const next = new Set(prev);
          next.add(surveyId);
          return next;
        });
        toast.success(response.data.message || "Survey saved successfully!");
      } else {
        toast.error(response?.data?.message || "Failed to save survey.");
      }
    } catch (error) {
      console.error("Error saving survey:", error);
      toast.error(
        error.response?.data?.message || "An error occurred while saving the survey."
      );
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center py-5">
        <div className="spinner-border text-danger" role="status" style={{ width: "3rem", height: "3rem" }}>
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 text-muted ft-15 fw-500">Loading your personalized surveys...</p>
      </div>
    );
  }

  if (surveys.length === 0) {
    return (
      <div className="text-center py-5 px-4 bg-white rounded-4 border" style={{ borderColor: "#ede7e1" }}>
        <div className="mb-4 text-danger">
          <i className="bi bi-inbox fs-1"></i>
        </div>
        <h4 className="ft-18 fw-bold text-dark">No surveys available</h4>
        <p className="text-muted ft-13 mt-2 max-w-md mx-auto">
          We are analyzing your profile to match you with active surveys. Please check back shortly or update your profile details.
        </p>
      </div>
    );
  }

  return (
    <>
      {surveys.map((item) => {
        const coverImg = item.survey_image && item.survey_image !== "string" ? item.survey_image : photolg;
        const rewardCoins = parseFloat(item.reward_per_participant) || 0;
        const tags = [item.sector, item.industry, item.country].filter(Boolean);

        return (
          <div className="post-card mb-4" key={item.id}>
            <div className="d-flex align-items-center mb-3">
              <img src={surveyPhoto(item)} onError={handlePhotoError} alt="" className="post-user-img" />

              <div className="ms-3">
                <h5 className="ft-15 fw-bold mb-1">Product Researcher</h5>

                <p className="ft-12 mb-0">
                  {formatPostedDate(item.created_on)}
                </p>
              </div>
            </div>

            <div className="position-relative mb-4">
              <img src={coverImg} alt="Cover Banner" className="post-banner" style={{ backgroundColor: "#f3f4f6" }} />

              <div className="reward-badge ft-11 d-flex gap-2 align-items-center">
                <img src={star} alt="star" /> Reward: {rewardCoins} Coins
              </div>
            </div>

            <h2 className="post-title fw-bold mb-3 ft-20">
              {item.survey_title}
            </h2>

            <p className="post-desc ft-12 mb-3">
              {item.survey_description}
            </p>

            <div className="d-flex flex-wrap gap-2 mb-4">
              {tags.map((tag, idx) => (
                <span className="tag-box" key={idx}>#{tag}</span>
              ))}
            </div>

            <div className="col-12 mt-16">
              <div className="d-flex align-items-center justify-content-between">

                <div className="d-flex gap-2 items-center">
                  
                  <button
                    type="button"
                    className="btn border-0 rounded-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: "40px",
                      height: "40px",
                      background: "#FFF4F4",
                    }}
                  >
                    <Share2 size={18} color="#2F343B" />
                  </button>

                  <button
                    type="button"
                    className="btn border-0 rounded-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: "40px",
                      height: "40px",
                      background: "#FFF4F4",
                    }}
                    onClick={() => handleSave(item.id)}
                    disabled={savingId === item.id}
                  >
                    <Bookmark
                      size={18}
                      color={savedIds.has(item.id) ? "#c72430" : "#2F343B"}
                      fill={savedIds.has(item.id) ? "#c72430" : "none"}
                    />
                  </button>

                </div>

                {item.participation_status === 1 ? (
                  <button
                    type="button"
                    className="w-33 fw-bold ft-14 px-5 py-3 rounded-2 cursor-target text-center border-0"
                    style={{
                      backgroundColor: "#E6F4EA",
                      color: "#137333",
                      cursor: "not-allowed",
                      pointerEvents: "none"
                    }}
                    disabled
                  >
                    Completed
                  </button>
                ) : (
                  <button
                    type="button"
                    className="w-33 fw-bold ft-14 text-white px-5 py-3 bgred rounded-2 cursor-target text-center hover:tw-bg-[#333] tw-transition-all tw-duration-300 tw-ease-in-out hover:tw-scale-105"
                    onClick={() => handleStart(item)}
                  >
                    Start Survey
                  </button>
                )}

              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default Dashboard;